import { Grid, Stack, Typography } from '@mui/material';
import { useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    ZenegyEmployeeTypeEnum,
    ZenegySalaryModeEnum,
    ZenegySalaryTypeEnum
} from '@/models/payment';
import { useErrorHandling } from '@/utils/errorHandling';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '@/components/shared/SearchInput';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { SMBold } from '@/typography/Typography';
import { SearchGrid } from '@/components/SearchGrid';
import { ActiveFilter } from '@/pages/users/UserActiveFilter';
import { useAppDispatch, usePayments } from '@/lib/hooks';
import {
    getCurrentPaymentCompany,
    getZenegyDepartments,
    InstaPaidSalaryTypes
} from '@/lib/slices/paymentsSlice';
import { SecondaryButtonRegular } from '@/components/shared/Button';
import {
    useEmployeeDepartments,
    useEmployeeTypes
} from '@/services/paymentsQuery';
import { setLoader } from '@/lib/slices/globalLoaderSlice';

export type ZenegyUsersFiltersProps = {
    selectedDepartments: string[];
    onDepartmentChange: (value: string[]) => void;
    selectedSalaryModes: ZenegySalaryModeEnum[];
    onSalaryModeChange: (value: string | string[]) => void;
    selectedSalaryTypes: ZenegySalaryTypeEnum[];
    onSalaryTypeChange: (value: string | string[]) => void;
    selectedEmployeeTypes: ZenegyEmployeeTypeEnum[];
    onEmployeeTypeChange: (value: string | string[]) => void;
    selectedPlanDayEmployeeTypes: string[];
    onPlanDayEmployeeTypeChange: (value: string | string[]) => void;
    username: string;
    onUsernameChange: (name: string) => void;
    selectedActiveType: string;
    onActiveTypeChange: (value: string | string[]) => void;
    selectedInstaPaidSalaryTypes: string[];
    onInstaPaidSalaryTypeChange: (value: string | string[]) => void;
};

export const UsersFilters = (props: ZenegyUsersFiltersProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const getEmployeeDepartments = useEmployeeDepartments();
    const getEmployeeTypes = useEmployeeTypes();

    const dispatch = useAppDispatch();
    const { backendErrorHandler } = useErrorHandling();
    const { t } = useTranslation();

    // Memoize department and employee type choices
    const departmentChoices = useMemo(
        () =>
            getEmployeeDepartments?.data?.data?.departments
                ?.map((department) => ({
                    label: department.departmentName,
                    value: department.departmentId
                }))
                .sort((a, b) => a.label?.localeCompare(b.label)) ?? [],
        [getEmployeeDepartments?.data?.data?.departments]
    );

    const employeeTypeChoices = useMemo(
        () =>
            getEmployeeTypes?.data?.data?.employeeTypes
                ?.map((type) => ({
                    label: type.employeeTypeName,
                    value: type.employeeTypeId
                }))
                .sort((a, b) => a.label?.localeCompare(b.label)) ?? [],
        [getEmployeeTypes?.data?.data?.employeeTypes]
    );

    // Helper to update URL params
    const updateSearchParam = useCallback(
        (key: string, value: string | string[]) => {
            const newParams = new URLSearchParams(searchParams);
            if (Array.isArray(value)) {
                newParams.delete(key);
                if (value.length > 0) {
                    newParams.set(key, value.join(','));
                }
            } else {
                if (value) {
                    newParams.set(key, value);
                } else {
                    newParams.delete(key);
                }
            }
            setSearchParams(newParams, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        dispatch(
            setLoader(
                getEmployeeTypes.isPending || getEmployeeDepartments.isPending
            )
        );
    }, [
        getEmployeeTypes.isPending,
        getEmployeeDepartments.isPending,
        dispatch
    ]);

    useEffect(() => {
        if (getEmployeeTypes.isError) {
            backendErrorHandler('Error fetching employee types ');
        }
    }, [getEmployeeTypes.isError, backendErrorHandler]);

    const handleEmployeeTypeChange = useCallback(
        (value: string[]) => {
            props.onEmployeeTypeChange(value);
            updateSearchParam('employeeTypes', value);
        },
        [props.onEmployeeTypeChange, updateSearchParam]
    );

    const handleDepartmentChange = useCallback(
        (value: string[]) => {
            props.onDepartmentChange(value);
            updateSearchParam('departments', value);
        },
        [props.onDepartmentChange, updateSearchParam]
    );

    const handleActiveTypeChange = useCallback(
        (value: string | string[]) => {
            props.onActiveTypeChange(value);
            updateSearchParam('activeType', value as string);
        },
        [props.onActiveTypeChange, updateSearchParam]
    );

    const handleInstaPaidSalaryTypeChange = useCallback(
        (value: string) => {
            props.onInstaPaidSalaryTypeChange(value);
            updateSearchParam('instaPaidSalaryType', value);
        },
        [props.onInstaPaidSalaryTypeChange, updateSearchParam]
    );

    const { zenegyDepartments, planDayEmployeeType, paymentCompany } =
        usePayments();

    useEffect(() => {
        if (zenegyDepartments.error) {
            backendErrorHandler('Error fetching departments: ');
        }
    }, [zenegyDepartments.error, backendErrorHandler]);

    useEffect(() => {
        if (planDayEmployeeType.error) {
            backendErrorHandler('Error fetching Planday employee types: ');
        }
    }, [planDayEmployeeType.error, backendErrorHandler]);

    useEffect(() => {
        if (paymentCompany.error) {
            backendErrorHandler('Error fetching company details: ');
        }
    }, [paymentCompany.error, backendErrorHandler]);

    useEffect(() => {
        dispatch(getZenegyDepartments());
        dispatch(getCurrentPaymentCompany());
    }, [dispatch]);

    const resetFilters = useCallback(() => {
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    return (
        <SearchGrid container columnSpacing={1}>
            <Grid item xs={2}>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={1}
                    justifyContent={'space-between'}
                >
                    <div>
                        <SMBold noWrap={true}>{t('Filter by: ')}</SMBold>
                    </div>

                    <SearchInput
                        value={props.username}
                        onDebouncedTextChange={props.onUsernameChange}
                    />
                </Stack>
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
                <Dropdown
                    label="Department"
                    choices={departmentChoices}
                    value={props.selectedDepartments}
                    type={DropdownType.Multi}
                    onChange={handleDepartmentChange}
                />
            </Grid>

            <Grid item xs={12} sm={2} md={1.5}>
                <Dropdown
                    label="Employee type"
                    choices={employeeTypeChoices}
                    type={DropdownType.Multi}
                    value={props.selectedEmployeeTypes}
                    onChange={handleEmployeeTypeChange}
                />
            </Grid>

            <Grid item xs={12} sm={2} md={1.5}>
                <Dropdown
                    label="Salary type"
                    type={DropdownType.Single}
                    choices={InstaPaidSalaryTypes}
                    value={props.selectedInstaPaidSalaryTypes}
                    onChange={handleInstaPaidSalaryTypeChange}
                />
            </Grid>

            <Grid item xs={12} sm={2} md={1.5}>
                <ActiveFilter
                    selectedActiveType={props.selectedActiveType}
                    onActiveTypeChange={handleActiveTypeChange}
                />
            </Grid>
            <Grid item>
                <SecondaryButtonRegular onClick={resetFilters}>
                    <Typography
                        fontSize={'14px'}
                        lineHeight={'20px'}
                        fontWeight={600}
                        noWrap
                    >
                        Reset
                    </Typography>
                </SecondaryButtonRegular>
            </Grid>
        </SearchGrid>
    );
};
